
import CloseIcon from "@material-ui/icons/Close";
import ErrorIcon from "@material-ui/icons/Error";
import SearchIcon from "@material-ui/icons/Search";
import SettingsIcon from "@material-ui/icons/Settings";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { SupportedTranslations } from "../../../../src/entities/BibleTranslation";
import { BuilderPreferences } from "../../../../src/entities/BuilderPreferences";
import { BibleVerseSearchItem, SearchService, SearchServiceHelper } from "../../../../src/services/SearchService";
import { TelemetryService } from "../../../../src/services/TelemetryService";
import { EventTracking, SearchEntity } from "../../../../src/utilities/Tracking/EventTracking";
import { DrawerContent } from "../../../entities/DrawerContent";
import classNames from "classnames";
import { makeStyles, ListSubheader, IconButton, Divider, Tooltip, TextField, InputAdornment, Button, CircularProgress, Typography, List, ListItemText, Hidden } from "@material-ui/core";
import axios, { CancelTokenStatic, CancelTokenSource } from "axios";

const useStyles = makeStyles((theme) =>({
	root: {},
	toolbar: theme.mixins.toolbar,
	title: {
		paddingTop: 10,
		paddingBottom: 7,
		textAlign: "left",
		fontSize: 22,
	},
	subtitle: {
		paddingTop: theme.spacing(1),
		textAlign: "left",
		fontSize: 15,
		textOverflow: "ellipsis",
	},
	closeIcon: {
		float: "right",
	},
	searchBar: {
		minHeight: "48px",
		maxHeight: "48px",
		width: "95%",
		backgroundColor: "#fff",
		borderRadius: "0",
		margin: "0 auto",
		marginTop: 10,
	},
	inputSearchText: {
		padding: "15px",
	},
	placeHolderIcon: {
		marginTop: "30px",
		width: "50px",
		height: "50px",
		color: "#CCC",
	},
	placeHolderText: {
		fontFamily: "Roboto, Helvetica",
		fontSize: "16px",
		marginTop: "12px",
		color: "#5f6368",
		textAlign: "center",
	},
	emptyState: {
		background: "#efefef",
		height: "100%",
	},
	searchWrapper: {
		display: "flex",
		background: "#efefef",
		minHeight: 60,
	},
	loading: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(1),
		marginLeft: "auto",
		marginRight: "auto",
		display: "block",
	},
	searchResults: {
		paddingBottom: 100,
		overflowY: "auto",
	},
	searchResultsAmp: {
		paddingBottom: 165,
		overflowY: "auto",
	},
	bibleCopyright: {
		padding: 5,
		paddingLeft: 16,
		paddingRight: 16,
		"& a": {
			color: theme.palette.primary.main,
		},
		position: "fixed",
		bottom: 0,
		backgroundColor: "white",
		borderTop: "1px solid #efefef",
		[theme.breakpoints.down("sm")]: {
			padding: "0 8px",
			"& .copyright": {
				fontSize: 11,
				lineHeight: "15px",
			},
		},
	},
	copyIcon: {
		display: "none",
		right: 0,
		top: "30%",
		"&:hover": {
			display: "block",
		},
	},
	result: {
		whiteSpace: "normal",
		height: "auto",
		"&:hover": {
			"&+div": {
				display: "block",
			},
		},
		padding: "5px 10px",
		"& sup": {
			verticalAlign: "baseline",
			position: "relative",
			top: "-0.2em",
			color: "#000",
			fontVariantNumeric: "diagonal-fractions",
		},
	},
	noVerseNumbers: {
		"& sup": {
			display: "none",
		},
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	copyInProgress: {
		margin: theme.spacing(1),
	},
	starIcon: {
		width: 13,
		height: 13,
	},
	verticalText: {
		transform: "rotate(-90deg)",
		width: "90px",
		transformOrigin: "45% 85%",
		textAlign: "center",
		lineHeight: "52px",
	},
	grow: {
		flex: 1,
	},
	settingsIcon: {
		float: "right",
		"&:hover": {
			backgroundColor: "rgb(145, 145, 145)",
		},
	},
	relatedButtons: {
		display: "flex",
		justifyContent: "flex-end",
		backgroundColor: "#efefef",
		paddingRight: 5,
		minHeight: 35,
	},
	scriptureActionButtons: {
		display: "flex",
		paddingBottom: 7,
		paddingLeft: 5,
	},
	actionButtons: {
		fontSize: 13,
		textTransform: "capitalize",
	},
	noSup: {
		display: "none",
	},
}));

interface Props{
	onDrawerToggle: (open: boolean) => void;
	onSettingsDialogToggle: () => void;
	onCopyVerse: (verseContent: string) => void;
	onBibleVerseSelected: (scriptureReference: string) => void;
	preferences: BuilderPreferences;
	onRelatedSearch: (type: DrawerContent, queryText: string) => void;
	onPreferenceChange: (preference: BuilderPreferences) => void;
}

type PropsWithClasses = Props & { classes: ReturnType<typeof useStyles> };

interface State {
	queryText: string;
	bibleVerseSearchResult?: BibleVerseSearchItem[];
	totalBibleVerseSearchResultCount: number;
	bibleVerseSearchServerError?: string;
	onCopyInProgress: boolean;
	open: boolean;
	isValidScriptureSearch: boolean;
	isChapterSearch: boolean;
	searchTimeOut?: any;
	queriedBibleTranslationId?: string;
}
const currentLocation = "Bible Drawer Interaction"

class BibleSearchComponent extends React.Component<PropsWithClasses, State> {
	cancellationTokenFactory: CancelTokenStatic;
    cancellationToken: CancelTokenSource;

	constructor(props: PropsWithClasses) {
		super(props);
		
		this.cancellationTokenFactory = axios.CancelToken;
        this.cancellationToken = this.cancellationTokenFactory.source();

		this.state = {
			queryText: "",
			totalBibleVerseSearchResultCount: 0,
			onCopyInProgress: false,
			open: false,
			isValidScriptureSearch: false,
			isChapterSearch: false,
			queriedBibleTranslationId: this.props.preferences.bibleTranslationId,
		};
	}

	componentDidUpdate(prevsProps: Props) {
		if (this.state.queriedBibleTranslationId !== prevsProps.preferences.bibleTranslationId) {
			this.setState({ queriedBibleTranslationId: prevsProps.preferences.bibleTranslationId }, this.startSearch);
		}
	}

	startSearch() {
		this.setState(
			{
				totalBibleVerseSearchResultCount: 0,
				bibleVerseSearchResult: undefined,
				bibleVerseSearchServerError: undefined,
			},
			this.searchBible
		);
		// We want to capture that the user searched something without spamming our server each keypress
		if (this.state.queryText.length === 1) {
			let now = new Date();
			TelemetryService.updateContactField("sc_cloud_explorer", now.toString());
		}
	}

	async searchBible() {
		const skip = this.state.bibleVerseSearchResult ? this.state.bibleVerseSearchResult.length : 0;
		const take = 10;
        
		if (skip === 0) {
            this.cancellationToken.cancel('Canceled By User');
            this.cancellationToken = this.cancellationTokenFactory.source()
        }

		const result = await SearchService.findBibleVerses(
			this.state.queryText,
			this.props.preferences.bibleTranslationId,
			skip,
			take,
			this.cancellationToken
		);

		let bibleVerseSearchResult = this.state.bibleVerseSearchResult ? this.state.bibleVerseSearchResult : [];

		for (let i = 0; i < result.results.length; i++) {
			bibleVerseSearchResult.push(result.results[i]);
		}

		this.setState(
			{
				bibleVerseSearchResult,
				totalBibleVerseSearchResultCount: result.totalCount,
				isValidScriptureSearch: result.isValidScriptureSearch,
				isChapterSearch: result.isChapter,
			},
			() => EventTracking.Builder.onSearch(SearchEntity.Bible, currentLocation)
		);
	}

	async onCopyVerse(selectedVerse: BibleVerseSearchItem) {
		let { bibleSearchCopySetting } = this.props.preferences;
		let formattedVerse = null;

		if (bibleSearchCopySetting === "all") {

			formattedVerse = `${this.correctVerseRefInText(selectedVerse)}\n${selectedVerse.title} - ${this.props.preferences.bibleTranslationId}`;
		} else if (bibleSearchCopySetting === "numbers") {
			formattedVerse = `${this.correctVerseRefInText(selectedVerse)}\n${selectedVerse.title} - ${this.props.preferences.bibleTranslationId}`;
			formattedVerse = SearchServiceHelper.removeTranslationNotes(formattedVerse);
		} else {
			formattedVerse = `${selectedVerse.description}\n${selectedVerse.title} - ${this.props.preferences.bibleTranslationId}`;
			formattedVerse = SearchServiceHelper.removeVerseNumbers(formattedVerse);
			formattedVerse = SearchServiceHelper.removeTranslationNotes(formattedVerse);
		}
		formattedVerse = formattedVerse.replace("&nbsp;", "");
		formattedVerse = formattedVerse.replace(/<(?:.|\n)*?>/gm, "");
		this.props.onCopyVerse(formattedVerse);
		EventTracking.Builder.onCopy(SearchEntity.Bible, currentLocation);
	}

	correctVerseRefInText(bibleVerseObj: BibleVerseSearchItem) {
		let formattedVerse = "";
		let selectedVerse = bibleVerseObj.description;
		if (selectedVerse.includes("<sup>")) {
			formattedVerse = selectedVerse.replace(/<sup>/g, "(");
			formattedVerse = formattedVerse.replace(/<\/sup>/g, ")");
			if(this.props.preferences.bibleSearchCopySetting === "all"){
				const footnoteStartRegex = /<footnote>/g;
				const footnoteEndRegex = /<\/footnote>/g;
				formattedVerse = formattedVerse.replace(footnoteStartRegex, " (Note- ");
				formattedVerse = formattedVerse.replace(footnoteEndRegex, ")");
			}
		} else {
			let verseNumber = bibleVerseObj.title.split(":")[1];
			let formattedVerseNumber = verseNumber ? `(${verseNumber}) ` : "";
			formattedVerse = formattedVerseNumber + selectedVerse;
		}
		return formattedVerse;
	}

	highlightText(text: string, match: string) {
		if (!text) {
			return "";
		}

		if (!match) {
			return text;
		}

		// Escape regex characters before passing into a regex
		match = match
			.trim()
			.replace(/ /gi, "|")
			.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

		const regexp = new RegExp("(" + match + ")", "gi");
		return text.replace(regexp, '<strong style="background-color: #ffff0063">$1</strong>');
	}

	onDrawerToggle = (open: boolean) => {
		this.setState({ open }, () => this.props.onDrawerToggle(open));
	};

	showHideVerseTextToggled = () => {
		this.props.onPreferenceChange({
			...this.props.preferences,
			bibleSearchShowVerseNumbers: this.props.preferences.bibleSearchShowVerseNumbers ? false : true,
		});
	};

	delaySearch = (e: any) => {
		if (this.state.searchTimeOut) {
			clearTimeout(this.state.searchTimeOut);
		}
		this.setState({
			queryText: e.target.value,
			searchTimeOut: setTimeout(() => {
				this.startSearch();
			}, 1000),
		});
	};

	render() {
		const classes = this.props.classes;
		const bibleResult = this.state.bibleVerseSearchResult;

		const bibleTranslation = SupportedTranslations.find((t) => t.id === this.props.preferences.bibleTranslationId!);
		const bibleCopyright = bibleTranslation && bibleResult && bibleResult.length > 0 ? bibleTranslation.copyrightHtml : undefined;

		return (
			<>
				<React.Fragment>
					<ListSubheader className={classes.title}>
						Search Bible
						<IconButton onClick={() => this.onDrawerToggle(false)} className={classes.closeIcon}>
							<CloseIcon />
						</IconButton>
					</ListSubheader>
					<Divider />
					<ListSubheader className={classes.subtitle} color="primary">
						<span style={{ cursor: "pointer" }} onClick={() => this.props.onSettingsDialogToggle()}>
							Bible Settings
						</span>
						<Tooltip title="Change Settings">
							<IconButton onClick={() => this.props.onSettingsDialogToggle()} className={classes.settingsIcon}>
								<SettingsIcon />
							</IconButton>
						</Tooltip>
					</ListSubheader>
					<div className={classes.searchWrapper}>
						<TextField
							placeholder="Search the Bible"
							variant="outlined"
							className={classes.searchBar}
							InputProps={{
								className: classes.inputSearchText,
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
								style: { height: 48 },
							}}
							value={this.state.queryText}
							onChange={(e) => this.delaySearch(e)}
						/>
					</div>
					{this.state.queryText.length > 0 && (
						<div className={classes.relatedButtons}>
							<Button
								size="small"
								className={classes.actionButtons}
								variant="text"
								color="primary"
								onClick={() => this.props.onRelatedSearch(DrawerContent.Sermon, this.state.queryText)}
							>
								Related Sermons
							</Button>
							<Button
								size="small"
								className={classes.actionButtons}
								variant="text"
								color="primary"
								onClick={() => this.props.onRelatedSearch(DrawerContent.Illustration, this.state.queryText)}
							>
								Related Illustration
							</Button>
						</div>
					)}
					<React.Fragment>
						{!this.state.queryText && <div className={classes.emptyState} />}
						{!this.state.bibleVerseSearchResult &&
							this.state.bibleVerseSearchServerError === undefined &&
							this.state.queryText.length > 0 && (
								<div>
									<CircularProgress className={classes.loading} size={35} />
									<br />
									<Typography variant="h5" align="center">
										Loading Bible Verses
									</Typography>
								</div>
							)}
						{this.state.bibleVerseSearchResult &&
							this.state.bibleVerseSearchResult.length > 0 &&
							this.state.bibleVerseSearchServerError === undefined &&
							this.state.queryText.length > 0 && (
								<List
									className={classNames(
										"customScrollbar",
										this.props.preferences.bibleTranslationId === "AMP" ? classes.searchResultsAmp : classes.searchResults
									)}
								>
									<InfiniteScroll
										loadMore={() => this.searchBible()}
										hasMore={this.state.totalBibleVerseSearchResultCount > this.state.bibleVerseSearchResult.length}
										useWindow={false}
										loader={
											<div key="loader">
												<CircularProgress className={classes.loading} size={35} />
												<br />
												<Typography variant="h5" align="center">
													Loading Bible Verses
												</Typography>
											</div>
										}
									>
										{this.state.bibleVerseSearchResult.map((bibleVerse) => {
											let { bibleSearchViewSetting } = this.props.preferences;
											let displayContent = bibleVerse.description;
											if (bibleSearchViewSetting !== "all") {
												displayContent = SearchServiceHelper.removeTranslationNotes(bibleVerse.description);
											} else {
												const footnoteStartRegex = /<footnote>/g;
												const footnoteEndRegex = /<\/footnote>/g;
												displayContent = displayContent.replace(footnoteStartRegex, "<em> (Note- ");
												displayContent = displayContent.replace(footnoteEndRegex, ")</em>");
											}

											let displayedVerseText = this.highlightText(displayContent, this.state.queryText);
											if (bibleSearchViewSetting !== "text" && !displayedVerseText.includes("<sup>")) {
												let verseNumber = bibleVerse.title.split(":")[1];
												displayedVerseText = `<sup>${verseNumber}</sup> ${displayedVerseText}`;
											}

											return (
												<React.Fragment key={bibleVerse.title}>
													<div
														className={classNames(
															classes.result,
															bibleSearchViewSetting !== "text" ? "" : classes.noVerseNumbers
														)}
													>
														<ListItemText
															primary={bibleVerse.title}
															secondary={
																<span
																	dangerouslySetInnerHTML={{
																		__html: displayedVerseText,
																	}}
																/>
															}
														/>
													</div>
													<div className={classes.scriptureActionButtons} style={{ paddingBottom: 0 }}>
														<Button
															size="small"
															className={classes.actionButtons}
															variant="text"
															color="primary"
															onClick={() => this.onCopyVerse(bibleVerse)}
														>
															Copy to clipboard
														</Button>
														{!this.state.isChapterSearch && (
															<Button
																size="small"
																className={classes.actionButtons}
																variant="text"
																color="primary"
																onClick={() => {
																	this.props.onBibleVerseSelected(bibleVerse.scriptureReference);
																	EventTracking.Builder.contentDrawerOpen(SearchEntity.Bible, currentLocation);
																}}
															>
																View full chapter
															</Button>
														)}
													</div>
													<Divider />
												</React.Fragment>
											);
										})}
									</InfiniteScroll>
								</List>
							)}
						{bibleCopyright && (
							<div className={classes.bibleCopyright}>
								<Hidden smDown>
									<Typography
										style={{ cursor: "pointer", textDecoration: "underline" }}
										color="primary"
										onClick={() => this.props.onSettingsDialogToggle()}
									>
										Change Bible translation
									</Typography>
								</Hidden>
								<Typography color="textSecondary" variant="caption" dangerouslySetInnerHTML={{ __html: bibleCopyright }} />
								<Divider />
							</div>
						)}
						{this.state.bibleVerseSearchResult &&
							this.state.bibleVerseSearchResult.length === 0 &&
							this.state.bibleVerseSearchServerError === undefined && (
								<div className={classes.placeHolderText}>
									<ErrorIcon className={classes.placeHolderIcon} />
									<br />
									No Bible Verses Found
								</div>
							)}
						{this.state.bibleVerseSearchServerError !== undefined && (
							<div className={classes.placeHolderText}>
								<ErrorIcon className={classes.placeHolderIcon} />
								<br />
								{this.state.bibleVerseSearchServerError}
							</div>
						)}
					</React.Fragment>
				</React.Fragment>
			</>
		);
	}
}

export function BibleSearch(props:Props){
	const classes = useStyles();
	return <BibleSearchComponent {...props} classes={classes} />;
}